import { useCallback } from "react"

import { SOFIA_DISPLAY_NAME } from "@/constants/sofia"
import { toast } from "@coval/ui"

import type {
  InvestigationFinding,
  InvestigationFindingAction,
} from "../utils/investigationFindings"
import { applyInvestigationFindingAction } from "./useInvestigationFindings"

const INVESTIGATION_ACTION_SUCCESS_COPY: Record<
  InvestigationFindingAction,
  string
> = {
  acknowledge: "Finding acknowledged.",
  dismiss: "Finding dismissed.",
  flag_wrong: `Flagged as wrong — ${SOFIA_DISPLAY_NAME} will remember this report.`,
  resolve:
    "Resolution recorded. Sofia will surface a new linked regression if the pattern returns.",
  link_remediation: "Remediation reference recorded.",
}

// Shared by every surface that changes a finding (Home briefing and investigation
// history), so the toast copy and the conflict/error handling cannot drift between
// them. Resolves true only when the action applied: callers keep anything the user
// typed whenever it returns false.
export function useInvestigationFindingActionHandler({
  organizationSlug,
  patchFinding,
  refetch,
  workspaceId,
}: {
  organizationSlug?: string | null
  patchFinding: (finding: InvestigationFinding) => void
  refetch: () => void
  workspaceId: string | null
}) {
  return useCallback(
    async (
      finding: InvestigationFinding,
      action: InvestigationFindingAction,
      note?: string,
    ) => {
      if (!workspaceId) {
        return false
      }
      const result = await applyInvestigationFindingAction({
        action,
        expectedVersion: finding.version,
        findingId: finding.findingId,
        note,
        organizationSlug,
        workspaceId,
      })
      if (result.kind === "applied") {
        // Commit the server's post-action copy immediately: the refetch below can
        // fail, and a stale status/version left in the snapshot would stay actionable
        // and make the next action conflict.
        if (result.finding) {
          patchFinding(result.finding)
        }
        toast.success(INVESTIGATION_ACTION_SUCCESS_COPY[action])
      } else if (result.kind === "conflict") {
        toast.warning(
          "This finding changed since you loaded it — refreshing to the latest version.",
        )
      } else {
        toast.danger("The finding update didn't go through. Try again.")
      }
      refetch()
      return result.kind === "applied"
    },
    [organizationSlug, patchFinding, refetch, workspaceId],
  )
}
